import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React__default, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import FocusTrap from 'focus-trap-react';
import { generateId, useDesignSystemThemeName } from '../utils.js';
import { GlobalModalStyles, animationSpeed, StyledOverlay, StyledModal, StyledHeader, StyledContent, StyledFooter, StyledCloseButton } from './Modal.styles.js';
import { ClearOutline24 } from '../../icons/24/index.js';
import { ButtonType, ButtonSize } from '../Button/Button.types.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var transitionStateMap = {
  entering: 'modal-enter-active',
  entered: 'modal-enter-done',
  exiting: 'modal-exit-active',
  exited: 'modal-exit-done',
  unmounted: ''
};

var getModalClassNames = function getModalClassNames(props, state, themeName) {
  var className = props.className,
      smallMode = props.smallMode;
  return "\n    modal\n    ".concat(className || '', "\n    ").concat(themeName, "\n    ").concat(smallMode ? 'small-mode' : '', "\n    ").concat(transitionStateMap[state] || '', "\n  ").replace(/\s*\n\s*/g, ' ');
};

var renderCloseIcon = function renderCloseIcon(props) {
  var hideCloseIcon = props.hideCloseIcon,
      onClose = props.onClose,
      onCancel = props.onCancel,
      closeLabel = props.closeLabel;

  if (hideCloseIcon) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null);
  }

  return /*#__PURE__*/React__default.createElement(StyledCloseButton, {
    className: "modal-close",
    type: ButtonType.TEXT,
    "aria-label": closeLabel,
    onClick: function onClick(event) {
      if (onClose) {
        onClose(event);
      }

      if (onCancel) {
        onCancel(event);
      }
    },
    icon: /*#__PURE__*/React__default.createElement(ClearOutline24, null),
    size: ButtonSize.LARGE
  });
};

var Modal = function Modal(props) {
  var dataTestId = props['data-test-id'],
      children = props.children,
      disableEscapeCloseEvent = props.disableEscapeCloseEvent,
      disableFocusTrap = props.disableFocusTrap,
      disableOverlayCloseEvent = props.disableOverlayCloseEvent,
      footer = props.footer,
      hideCloseIcon = props.hideCloseIcon,
      initialFocusElement = props.initialFocusElement,
      modalHasClosed = props.modalHasClosed,
      onCancel = props.onCancel,
      onClose = props.onClose,
      style = props.style,
      targetElement = props.targetElement,
      title = props.title,
      visible = props.visible,
      zIndex = props.zIndex;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      element = _useState2[0],
      setElement = _useState2[1];

  useEffect(function () {
    var el = document.createElement('div');
    el.className = 'sok-modal';
    (targetElement || document.body).appendChild(el);
    setElement(el);
    return function () {
      (targetElement || document.body).removeChild(el);
    };
  }, [setElement, targetElement]);

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      activeFocusTrap = _useState4[0],
      setActiveFocusTrap = _useState4[1];

  var _useState5 = useState(generateId()),
      _useState6 = _slicedToArray(_useState5, 1),
      id = _useState6[0];

  var themeName = useDesignSystemThemeName();
  var handleOverlayClick = useCallback(function (event) {
    if (disableOverlayCloseEvent || !onClose) {
      return;
    }

    onClose(event);

    if (onCancel) {
      onCancel(event);
    }
  }, [disableOverlayCloseEvent, onCancel, onClose]);
  var handleEscapeButtonClick = useCallback(function (event) {
    if (event.key !== 'Escape' || disableEscapeCloseEvent || !onClose) {
      return;
    }

    onClose(event);

    if (onCancel) {
      onCancel(event);
    }
  }, [disableEscapeCloseEvent, onCancel, onClose]);
  useEffect(function () {
    if (visible) {
      document.body.classList.add('sok-modal-open');
      window.addEventListener('keyup', handleEscapeButtonClick);
    } else {
      document.body.classList.remove('sok-modal-open');
      window.removeEventListener('keyup', handleEscapeButtonClick);
    }

    return function () {
      document.body.classList.remove('sok-modal-open');
      window.removeEventListener('keyup', handleEscapeButtonClick);
    };
  }, [handleEscapeButtonClick, visible]); // For SSR do not render any content.

  if (!element || typeof document === 'undefined' || typeof window === 'undefined') {
    return null;
  }

  return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(GlobalModalStyles, null), /*#__PURE__*/React__default.createElement(CSSTransition, {
    mountOnEnter: true,
    unmountOnExit: true,
    appear: true,
    "in": visible,
    timeout: animationSpeed,
    classNames: "modal"
  }, /*#__PURE__*/React__default.createElement(StyledOverlay, {
    className: "modal-overlay",
    style: {
      zIndex: zIndex
    },
    onMouseUp: function onMouseUp(event) {
      handleOverlayClick(event);
    }
  })), /*#__PURE__*/React__default.createElement(CSSTransition, {
    mountOnEnter: true,
    unmountOnExit: true,
    appear: true,
    "in": visible,
    timeout: animationSpeed,
    onEntered: function onEntered() {
      return setActiveFocusTrap(true);
    },
    onExiting: function onExiting() {
      return setActiveFocusTrap(false);
    },
    onExited: modalHasClosed,
    classNames: "modal"
  }, function (state) {
    var _Modal$defaultProps;

    return /*#__PURE__*/React__default.createElement(FocusTrap, {
      active: disableFocusTrap ? false : activeFocusTrap,
      focusTrapOptions: {
        initialFocus: initialFocusElement
      }
    }, /*#__PURE__*/React__default.createElement(StyledModal, {
      "data-test-id": dataTestId,
      style: _objectSpread({
        zIndex: zIndex ? zIndex + 1 : (_Modal$defaultProps = Modal.defaultProps) === null || _Modal$defaultProps === void 0 ? void 0 : _Modal$defaultProps.zIndex
      }, style),
      className: getModalClassNames(props, state, themeName),
      role: "dialog",
      "aria-labelledby": "modal-title-".concat(id),
      "aria-describedby": "modal-body-".concat(id)
    }, title && /*#__PURE__*/React__default.createElement(StyledHeader, {
      className: "modal-header",
      hasCloseIcon: !hideCloseIcon
    }, /*#__PURE__*/React__default.createElement("h1", {
      id: "modal-title-".concat(id)
    }, title)), !hideCloseIcon && renderCloseIcon(props), /*#__PURE__*/React__default.createElement(StyledContent, {
      className: "modal-content",
      id: "modal-body-".concat(id)
    }, children), footer && /*#__PURE__*/React__default.createElement(StyledFooter, {
      className: "modal-footer"
    }, footer)));
  })), element);
};

Modal.defaultProps = {
  closeLabel: 'Close',
  'data-test-id': 'Modal',
  zIndex: 1000,
  disableFocusTrap: false
};

var ConfirmModal = function ConfirmModal(props) {
  var newProps = _objectSpread(_objectSpread({}, props), {}, {
    smallMode: true
  });

  return /*#__PURE__*/React__default.createElement(Modal, newProps);
};

export { ConfirmModal, Modal };
//# sourceMappingURL=Modal.js.map
